import { Modal } from '@components/Modal';
import { Box, FormControlLabel, Stack, Typography } from '@mui/material';
import { FC, InputHTMLAttributes, useEffect, useState } from 'react';
import { Controller, get, useForm } from 'react-hook-form';
import { schema } from './schema';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { channels, defaultValues, priorities } from './B2cNotificationModal.consts';
import { DeliveryChannel, NotificationPriority, notificationApiSlice } from '@src/api';
import { SegmentedControl } from '@components/SegmentedControl';
import { Label } from '@components/Label';
import { ButtonGroup } from '@components/ButtonGroup';
import {
  border,
  buildDataTestId,
  buildDataTestIdAttribute,
  buildFullDataTestId,
  log,
  replaceCebabCase,
} from '@src/utils';
import { Checkbox } from '@components/Checkbox';
import { FormTextAreaField, FormTextField } from '@components/FormFields';
import { DropFiles } from './DropFiles';
import { B2CNotificationData } from '@pages/Selections/RowSelection';
import { PreloaderBubbles } from '@components/PreloaderBubbles';
import { theme } from '@src/theme';

interface B2CNotificationModalProps {
  b2cNotificationData: B2CNotificationData;
  setOpen: (val: Nullable<B2CNotificationData>) => void;
}

export const B2cNotificationModal: FC<B2CNotificationModalProps> = ({ b2cNotificationData, setOpen }) => {
  const { description = '', generationTaskId, patientsCount } = b2cNotificationData;
  const [createNotification] = notificationApiSlice.useLazyCreateB2CNotificationsQuery();
  const { data: notifictaionInfo, isLoading } = notificationApiSlice.useGetB2CPreviewQuery('');
  const [preview, setPreview] = useState(false);

  const dataTestId = buildFullDataTestId('modal-b2c-notification');

  const {
    control,
    setValue,
    getValues,
    watch,
    trigger,
    formState: { isDirty },
  } = useForm<yup.InferType<typeof schema>>({
    resolver: yupResolver(schema),
    defaultValues: { ...defaultValues, description },
    mode: 'onTouched',
  });

  const [openCancelModal, setOpenCancelModal] = useState(false);

  const onCancel = () => {
    if (isDirty) {
      setOpenCancelModal(true);
    } else {
      setOpen(null);
    }
  };

  const onApply = () => {
    if (preview) {
      const { notificationPriority, deliveryChannelTypes, description: descriptionValue, ...rest } = getValues();

      const descriptionWithNewLine = descriptionValue.replaceAll(/\r\n|\r|\n/g, '<br>');
      const data = {
        ...rest,
        notificationPriority: notificationPriority as NotificationPriority,
        deliveryChannelTypes: deliveryChannelTypes as DeliveryChannel[],
        generationTaskId: generationTaskId || '',
      };
      const formData = new FormData();
      formData.append('GenerationTaskId', `${data.generationTaskId}`);
      formData.append('NotificationPriority', data.notificationPriority);
      data.deliveryChannelTypes.forEach(type => formData.append('DeliveryChannelTypes', type));
      formData.append('Subject', data.subject);
      formData.append('Description', descriptionWithNewLine);
      formData.append('attachmentFile', data.attachmentFile || '');
      createNotification(formData)
        .then(() => setOpen(null))
        .catch(log);
    } else {
      trigger()
        .then(check => {
          if (check) {
            setPreview(true);
          }
          return null;
        })
        .catch(log);
    }
  };

  useEffect(() => {
    if (b2cNotificationData && notifictaionInfo) {
      const patientDescription = description ? `${description}\n\n` : '';
      const {
        creatorFirstName = '',
        creatorMiddleName = '',
        creatorLastName = '',
        creatorCompanyName = '',
        creatorCompanyAddress = '',
      } = notifictaionInfo;

      const creatorMiddleNameStr = creatorMiddleName ? `${creatorMiddleName} ` : '';
      const signature =
        typeof notifictaionInfo === 'object' && Object.values(notifictaionInfo).some(val => !!val)
          ? `Best regards,\n${creatorFirstName} ${creatorMiddleNameStr}${creatorLastName}\n${creatorCompanyName}\n${creatorCompanyAddress}`
          : '';
      setValue('description', `${patientDescription}${signature}`);
    }
  }, [b2cNotificationData, notifictaionInfo, description, setValue]);

  const selectedDeliveryChannels = watch('deliveryChannelTypes') || [];

  return (
    <>
      <Modal
        open={!!b2cNotificationData}
        title="Notification properties"
        bodySx={{ padding: 0 }}
        onCancel={onCancel}
        wrapperProps={{
          PaperProps: {
            sx: {
              maxWidth: '880px',
              maxHeight: '730px',
              width: '100%',
              height: '100%',
            },
            ...buildDataTestIdAttribute(dataTestId),
          },
        }}
      >
        <Box padding="24px" height="calc(100% - 96px)" position="relative">
          {isLoading && <PreloaderBubbles sx={{ backgroundColor: theme.palette.background.paper }} />}
          {preview ? (
            <Stack>
              <Box padding="12px 0">
                <Typography
                  color="grey.700"
                  variant="body16Medium"
                  lineHeight="20px"
                >{`Are you sure you want to send the notification to ${patientsCount || 0} patients?`}</Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }} />
            </Stack>
          ) : (
            <>
              <Stack direction="row" gap="96px">
                <Stack gap="12px">
                  <Label label="Priority" required />
                  <Controller
                    name="notificationPriority"
                    control={control}
                    render={({ field: { value, onChange } }) => {
                      const handleClick = (valueT: NotificationPriority) => {
                        onChange(valueT);
                      };
                      return (
                        <SegmentedControl
                          values={priorities}
                          active={value}
                          handleClick={handleClick}
                          dataTestId={buildFullDataTestId(dataTestId, 'priority')}
                        />
                      );
                    }}
                  />
                </Stack>
                <Stack gap="12px">
                  <Label label="Delivery channels" required />
                  <Controller
                    name="deliveryChannelTypes"
                    control={control}
                    render={({ field: { value: fieldValue, onChange }, formState }) => {
                      const errorField = get(formState.errors, 'deliveryChannelTypes');
                      const error = errorField ? (errorField.message as string) : '';

                      return (
                        <Box>
                          <Stack direction="row" gap="24px">
                            {channels.map(({ name, value }) => {
                              const disabledChannel = value === DeliveryChannel.VivicaNotifications;

                              const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                                const { checked: checkedEvent } = event.target;
                                if (checkedEvent) {
                                  onChange([...selectedDeliveryChannels, value]);
                                } else {
                                  onChange(
                                    selectedDeliveryChannels.filter(selectedChannels => selectedChannels !== value),
                                  );
                                }
                              };
                              return (
                                <FormControlLabel
                                  key={value}
                                  labelPlacement="end"
                                  disabled={disabledChannel}
                                  label={
                                    <Typography
                                      color={disabledChannel ? 'grey.300' : 'grey.700'}
                                      variant="label14Medium"
                                      lineHeight="18px"
                                    >
                                      {name}
                                    </Typography>
                                  }
                                  sx={{ gap: '3px' }}
                                  control={
                                    <Checkbox
                                      error={!!error}
                                      checked={fieldValue?.includes(value)}
                                      onChange={handleChange}
                                      inputProps={
                                        {
                                          ...buildDataTestIdAttribute(
                                            dataTestId,
                                            `channels-${replaceCebabCase(value)}`,
                                          ),
                                        } as InputHTMLAttributes<HTMLInputElement>
                                      }
                                    />
                                  }
                                />
                              );
                            })}
                          </Stack>
                          {error && (
                            <Typography
                              component="p"
                              color="error.200"
                              variant="label12Medium"
                              lineHeight="16px" /* sx={{ height: 0 }} */
                            >
                              Select at least one
                            </Typography>
                          )}
                        </Box>
                      );
                    }}
                  />
                </Stack>
              </Stack>
              <Box mt="24px">
                <FormTextField
                  name="subject"
                  label="Subject"
                  control={control}
                  required
                  maxLength={1000}
                  withMaxLenghtHelper
                  withErrorMessage
                  dataTestId={buildDataTestId(dataTestId, 'input-subject')}
                />
              </Box>
              <Box mt="30px" mb="30px">
                <FormTextAreaField
                  name="description"
                  placeholder=""
                  label="Description"
                  control={control}
                  required
                  maxLength={2500}
                  dataTestId={buildDataTestId(dataTestId, 'input-description')}
                />
              </Box>
              <DropFiles setValue={setValue} dataTestId={dataTestId} />
            </>
          )}
        </Box>
        <ButtonGroup
          applyName={`Send to ${patientsCount || 0} patients`}
          onApply={onApply}
          onCancel={onCancel}
          cancelName="Cancel"
          sx={{ borderTop: border, padding: '24px', marginLeft: 0 }}
          disableApply={isLoading}
          dataTestId={dataTestId}
        />
      </Modal>
      <Modal open={openCancelModal} title="Delete selection">
        <Box padding="12px 0 36px">
          <Typography>All changes will be lost. Proceed?</Typography>
        </Box>
        <ButtonGroup
          onApply={() => {
            setOpen(null);
          }}
          onCancel={() => {
            setOpenCancelModal(false);
          }}
          applyName="Yes"
          cancelName="No"
          dataTestId={buildDataTestId(dataTestId, 'modal-cancel')}
        />
      </Modal>
    </>
  );
};
